// Public status page — no auth required. Polls /api/status every 5s.
//
// Renders service-health cards (Backend, Database, WebSocket, Telephony) with
// live indicators. Useful for ops dashboards, partner devs, uptime monitors.
import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Database, Globe, Radio, Server, Activity, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function StatusIcon({ status, className = "w-5 h-5" }) {
  if (status === "ok") return <CheckCircle2 className={`${className} text-emerald-500`} />;
  if (status === "degraded" || status === "down") return <AlertTriangle className={`${className} text-amber-500`} />;
  return <XCircle className={`${className} text-red-500`} />;
}

function Pill({ status }) {
  const cls =
    status === "ok"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
      : status === "degraded"
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
      : "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40";
  const label = status === "ok" ? "Operational" : status === "degraded" ? "Degraded" : status === "down" ? "Down" : status;
  return <Badge variant="outline" className={`${cls} text-[11px] font-medium px-2 py-0.5`} data-testid={`pill-${status}`}>{label}</Badge>;
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    let alive = true;
    async function fetchStatus() {
      try {
        const r = await fetch(`${BACKEND}/api/status`, { cache: "no-store" });
        const j = await r.json();
        if (!alive) return;
        setData(j); setError(null); setLastChecked(new Date());
      } catch (e) {
        if (!alive) return;
        setError(String(e)); setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    }
    fetchStatus();
    const id = setInterval(fetchStatus, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const overall = data?.status || (error ? "down" : "loading");
  const components = data?.components || {};
  const counts = data?.counts || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8" data-testid="status-header">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1">
              <Activity className="w-3 h-3" /> System status
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Connect7 — Live Status</h1>
          </div>
          {loading && !data ? (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : (
            <Pill status={overall} />
          )}
        </div>

        {/* Overall banner */}
        <Card
          className={`mb-6 p-5 border-l-4 ${
            overall === "ok"
              ? "border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20"
              : overall === "degraded"
              ? "border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20"
              : "border-l-red-500 bg-red-50/50 dark:bg-red-950/20"
          }`}
          data-testid="status-banner"
        >
          <div className="flex items-center gap-3">
            <StatusIcon status={overall} className="w-6 h-6 shrink-0" />
            <div>
              <div className="font-semibold">
                {overall === "ok" && "All systems operational"}
                {overall === "degraded" && "Some systems are experiencing issues"}
                {overall === "down" && "Major outage — service unavailable"}
                {overall === "loading" && "Checking status…"}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {data?.uptime && <>Uptime: <span className="font-mono">{data.uptime}</span> · </>}
                {data?.version && <>Version: <span className="font-mono">{data.version}</span> · </>}
                {lastChecked && <>Last checked: {lastChecked.toLocaleTimeString()}</>}
              </div>
            </div>
          </div>
          {error && (
            <div className="mt-3 text-xs text-red-600 dark:text-red-400 font-mono break-all">
              {error}
            </div>
          )}
        </Card>

        {/* Component grid */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <StatusCard
            icon={Server} label="Backend (FastAPI)" component={components.backend}
            sub={components.backend?.uptime ? `Uptime: ${components.backend.uptime}` : ""}
            testid="card-backend"
          />
          <StatusCard
            icon={Database} label="Database (MongoDB)" component={components.database}
            sub={components.database?.error || (counts.tenants ? `${counts.tenants} tenants · ${counts.agents} agents` : "")}
            testid="card-database"
          />
          <StatusCard
            icon={Globe} label="WebSocket fan-out" component={components.websocket}
            sub={components.websocket ? `${components.websocket.active_clients || 0} live clients` : ""}
            testid="card-websocket"
          />
          <StatusCard
            icon={Radio} label="Telephony (Asterisk)" component={components.telephony}
            sub={components.telephony ? `Mode: ${components.telephony.mode}` : ""}
            testid="card-telephony"
          />
        </div>

        {/* Counts strip */}
        {Object.keys(counts).length > 0 && (
          <Card className="p-5" data-testid="counts-card">
            <div className="text-overline text-muted-foreground mb-3">Workload snapshot</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { k: "Tenants", v: counts.tenants },
                { k: "Agents", v: counts.agents },
                { k: "Contacts", v: counts.contacts },
                { k: "CDRs (all-time)", v: counts.cdrs },
              ].map((x) => (
                <div key={x.k} data-testid={`count-${x.k.toLowerCase().split(" ")[0]}`}>
                  <div className="text-overline text-muted-foreground">{x.k}</div>
                  <div className="font-display font-bold text-2xl tabular-nums">{(x.v ?? 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Footer links */}
        <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <a href="/api/docs" className="underline hover:text-foreground" data-testid="link-swagger">API Docs (Swagger)</a>
          <a href="/api/redoc" className="underline hover:text-foreground" data-testid="link-redoc">ReDoc</a>
          <a href="/api/openapi.json" className="underline hover:text-foreground" data-testid="link-openapi">OpenAPI spec</a>
          <span className="ml-auto inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Auto-refresh every 5s
          </span>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon: Icon, label, component, sub, testid }) {
  const status = component?.status || "loading";
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center shrink-0">
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="font-medium text-sm truncate">{label}</div>
          <StatusIcon status={status} className="w-4 h-4 shrink-0 ml-auto" />
        </div>
        <div className="text-xs text-muted-foreground truncate mt-0.5">{sub || "—"}</div>
      </div>
    </Card>
  );
}
